import React, { Fragment } from 'react';
import Drawer from 'rmc-drawer';
import { fabric } from 'sunzi-fabric';
import classnames from 'classnames';
import { Header, Sidebar, Edit, Guide, Wrapper } from '@/components';
import { Shop, ValidatorStatus, PasterOption, LineTextOption } from '@/sunzi';
import { MugType, MugModule, MugBottomImage, MugModuleState, SynchroObjects } from './mug';
import { i18n, getToken7n, uuid, Zen3d, dateTime, upload7nB64, loadHTMLImage } from '@/utils';
import { mugFBX, plasticCupFBX, vacuumCupFBX, pillowFBX, sockFBX, canvasFBX, hCanvasFBX, vCanvasFBX, discFBX } from '@/config';
import Content from './content';
import Bottom from './bottom';
import Preview from './preview';
import OOP from './oop';
import styles from './style.less';
import 'rmc-drawer/assets/index.css';
import '@/global.less';

/** 编辑模式类型 */
enum EditType {
  Paster,
  Text
}

interface ConfirmOption {
  pasters: {
    id: number;
    annexs: number[];
  }[];
  lineTexts?: {
    value: string,
    font: string
  }[];
}

interface MugProps {
  /** 店铺信息 */
  shop: Shop;
  /** 价格 */
  price: string;
  /** 产品标题 */
  title: string;
  /** 产品类型 */
  type?: MugType;
  /** 模块数据 */
  modules: MugModule[];
  /** 引导视频地址 */
  pathGuide: string;
  /** 可选底图 */
  bottomImages: MugBottomImage[];
  /** 默认变体值 */
  defaultOptionValue: string;
  /** 操作提示文案 */
  tips: string[];
  /** 操作水印地址 */
  watermarkURL?: string;
  /** 生成没有底图的结果图 */
  resultTransparent?: boolean;
  /** 七牛上传前缀 */
  path7nPrefix?: string;
  /** 关闭回调函数 */
  onClose: (optionValue?: string) => void;
  /** 确认回调函数 */
  onConfirm: (
    result?: string[],
    data?: {
      option: ConfirmOption[],
      bottomImage?: number,
    },
    optionValue?: string,
  ) => void;
  /** ga统计对象 */
  gtag: any;
}

interface MugState {
  /** 当前激活的模块索引 */
  current: number;
  /** 当前编辑类型 */
  editType?: EditType;
  /** 当前激活的底图 */
  currentBottomImage: MugBottomImage;
  /** 当前激活的贴纸 */
  currentPaster?: PasterOption;
  /** 当前激活的刻字 */
  currentText?: LineTextOption;
  /** 抽屉的显隐 */
  drawerVisible: boolean;
  /** 错误提示的显隐 */
  oopVisible: boolean;
  /** 模块信息 */
  moduleState: MugModuleState[];
  /** 3d的显隐 */
  previewVisible: boolean;
  /** 3d的预览的base64 */
  previewSource?: string;
  /** 引导的显隐 */
  guideVisible: boolean;
  /** 生成图片地址 */
  result?: string[];
  /** 当前画布内的canvas子元素 */
  synchroObjects: SynchroObjects[][];
  /** 当前画布内的激活canvas子元素 */
  synchroActiveObject: (fabric.Object | undefined)[];
  /** 底部layout显示隐藏 */
  bottomLayoutVisible: boolean;
  /** 全局上传显示隐藏 */
  uploadVisible: boolean;
}

class Mug extends React.Component<MugProps, MugState> {

  private _fbx: any;
  private _content: Array<Content> = [];
  private _canvas: HTMLCanvasElement;
  private _zen3d: Zen3d | undefined;
  private _preview3d: boolean;

  constructor(props: MugProps) {
    super(props);
    this._canvas = document.createElement("canvas");
    this._preview3d = true;
    const { bottomImages, defaultOptionValue } = props;
    // 引导 第一次进入的时候 主动弹出
    const guideVisible = !!parseInt(window.localStorage.getItem("_sunzi_mug_guide") || "1");
    const moduleState = this.handleDefaultOptionValue(defaultOptionValue);
    const synchroObjects = Array.from({ length: moduleState.length }).map(_ => []);
    this.state = {
      current: 0,
      currentBottomImage: bottomImages[0],
      drawerVisible: false,
      oopVisible: false,
      previewVisible: false,
      moduleState,
      guideVisible,
      synchroObjects,
      synchroActiveObject: [],
      bottomLayoutVisible: false,
      uploadVisible: false,
    }
  }

  static defaultProps = {
    preview3d: true,
    resultTransparent: false,
    type: MugType.MUG
  }

  componentDidMount() {
    // 处理不同产品类型的差异性
    this.handleDifference();
    const { shop, gtag } = this.props;
    i18n.setLocal(shop.language);
    this.setState({});
    gtag('event', 'screen_view', {
      'screen_name': shop.code
    });
  }

  componentWillReceiveProps(nextProps: MugProps) {
    if (this.props.defaultOptionValue !== nextProps.defaultOptionValue)
      this.setState({
        moduleState: this.handleDefaultOptionValue(nextProps.defaultOptionValue)
      });
  }

  /** 处理默认变体值 */
  handleDefaultOptionValue = (defaultOptionValue: string) => this.props.modules.map((item, index) => {
    let optionId = item.options[0].id;
    let validator: {
      status: ValidatorStatus;
      message: React.ReactNode[];
    } = {
      status: ValidatorStatus.waiting,
      message: []
    };
    if (index === 0) {
      // 如果有默认数据 并且默认数据存在于options中
      const defaultOption = item.options.find(option => option.value === defaultOptionValue);
      if (defaultOption)
        optionId = defaultOption.id;
      // 校验
      validator = this._moduleValidator({
        ...item,
        optionId,
        validator
      });
    }
    return {
      ...item,
      optionId,
      validator
    };
  });

  /** 处理不同产品类型的差异性 */
  handleDifference = async () => {
    const { type } = this.props;
    switch(type) {
      case MugType.VACUUM_CUP: // 保温杯
        this._canvas.width = 1500;
        this._canvas.height = 600;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface10'],
          spotLight: true
        });
        this._zen3d.loadFBX(vacuumCupFBX);
        this._fbx = vacuumCupFBX;
        break;
      case MugType.PLASTIC_CUP: // 塑料杯
        this._canvas.width = 1600;
        this._canvas.height = 1000;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface1'],
          spotLight: true
        });
        this._zen3d.loadFBX(plasticCupFBX);
        this._fbx = plasticCupFBX;
        break;
      case MugType.DIGITAL_IMAGE: // 数字图片
        this._canvas.width = 1000;
        this._canvas.height = 1000;
        this._preview3d = false;
        break;
      case MugType.PILLOW: // 抱枕双面
        this._canvas.width = 600;
        this._canvas.height = 600;
        this._zen3d = new Zen3d({
          objectNames: ['Pattern2D_9726', 'Pattern2D_9727'],
          spotLight: true
        });
        this._zen3d.loadFBX(pillowFBX);
        this._fbx = pillowFBX;
        break;
      case MugType.sPILLOW: // 抱枕单面
        this._canvas.width = 600;
        this._canvas.height = 600;
        this._zen3d = new Zen3d({
          objectNames: ['Pattern2D_9726'],
          colorNames: [{
            name: 'Pattern2D_9727', color: 0xffffff
          }],
          spotLight: true,
          ambientLightOpacity: 1
        });
        this._zen3d.loadFBX(pillowFBX);
        this._fbx = pillowFBX;
        break;
      case MugType.SOCK: // 袜子
        this._canvas.width = 531;
        this._canvas.height = 2096;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface1', 'polySurface4']
        });
        this._zen3d.loadFBX(sockFBX);
        this._fbx = sockFBX;
        break;
      case MugType.CANVAS: // 油画
        this._canvas.width = 600;
        this._canvas.height = 600;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface8'],
          spotLight: true
        });
        this._zen3d.loadFBX(canvasFBX);
        this._fbx = canvasFBX;
        break;
      case MugType.hCANVAS: // 油画 横版
        this._canvas.width = 840;
        this._canvas.height = 600;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface23'],
          spotLight: true
        });
        this._zen3d.loadFBX(hCanvasFBX);
        this._fbx = hCanvasFBX;
        break;
      case MugType.vCANVAS: // 油画 竖版
        this._canvas.width = 600;
        this._canvas.height = 750;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface23'],
          spotLight: true
        });
        this._zen3d.loadFBX(vCanvasFBX);
        this._fbx = vCanvasFBX;
        break;
      case MugType.sCANVAS: // 方形油画
        this._canvas.width = 1000;
        this._canvas.height = 1000;
        this._preview3d = false;
        break;
      case MugType.vBLANKET: // 毯子 竖版
        this._canvas.width = 1080;
        this._canvas.height = 1522;
        this._preview3d = false;
        break;
        case MugType.cTAPESTRY: // 圣诞挂毯
        this._canvas.width = 560;
        this._canvas.height = 800;
        this._preview3d = false;
        break;
      case MugType.DISC: // 小圆盘
        this._canvas.width = 531;
        this._canvas.height = 531;
        this._preview3d = false;
        break;
      case MugType.PUZZLE: // 拼图
        this._canvas.width = 746;
        this._canvas.height = 497;
        this._preview3d = false;
        break;
      case MugType.WHITE_aCANVAS: // 白底油画a
        this._canvas.width = 699;
        this._canvas.height = 932;
        this._preview3d = false;
        break;
      case MugType.TAPESTRY: // 挂毯
        this._canvas.width = 600;
        this._canvas.height = 750;
        this._preview3d = false;
        break;
      case MugType.GREETING_CARD: // 贺卡
        this._canvas.width = 1050;
        this._canvas.height = 750;
        this._preview3d = false;
        break;
      case MugType.TSHIRT: // 小人T恤衫
        this._canvas.width = 708;
        this._canvas.height = 708;
        this._preview3d = false;
        break;
      case MugType.ORNAMENT: // 挂饰
        this._canvas.width = 1343;
        this._canvas.height = 982;
        this._preview3d = false;
        break;
      default:
        this._canvas.width = 1400;
        this._canvas.height = 560;
        this._zen3d = new Zen3d({
          objectNames: ['polySurface1'],
          spotLight: true
        });
        this._zen3d.loadFBX(mugFBX);
        this._fbx = mugFBX;
        break;
    }
  }

  /**
   * 将base64传给7n
   */
  b64Upload7n = async (result: string, transparent: string): Promise<string[]> => {
    this.props.gtag('event', 'mug_upload_result', {
      'event_category': 'mug',
    });
    const { resultTransparent, type = MugType.MUG, path7nPrefix } = this.props;
    const token = await getToken7n();
    const prefix = path7nPrefix || `mug/${type.toLocaleLowerCase()}/`;
    const encodeKey = `${prefix}${dateTime}/${uuid()}`;
    const uploadQueue = [];
    // 正常结果图
    uploadQueue.push(upload7nB64({ base64: result, token, key: `${encodeKey}-0.png` }));
    // 透明结果图
    if (resultTransparent)
      uploadQueue.push(upload7nB64({ base64: transparent, token, key: `${encodeKey}-1.png` }));
    const response = await Promise.all(uploadQueue);
    return response.map(item => host7n['prefix-na0'] + item.key);
  }

  renderDrawer = () => {
    const { modules } = this.props;
    const { current, editType, currentPaster, currentText } = this.state;
    const { pasters, rules } = modules[current];

    switch(editType) {
      case EditType.Paster:
        return currentPaster && (
          <Edit.Paster
            active={currentPaster}
            dataSource={pasters}
            onApply={this.handlPasterApply}
          />
        );
      case EditType.Text:
        return currentText && (
          <Edit.Text
            active={currentText}
            rule={rules.lineText}
            onApply={this.handleTextApply}
          />
        );
      default:
        return null;
    }
  }

  renderContent = () => {
    const { type, watermarkURL } = this.props;
    const { current, currentBottomImage, moduleState } = this.state;
    return moduleState.map((item, index) => {
      const { rules, options, pastersConnect, optionId } = item;
      let pasters: PasterOption[] = [];
      let lineTexts: LineTextOption[] = [];
      // 如果存在默认选项
      if (options.length > 0) {
        pasters = options[0].pasters;
        lineTexts = options[0].lineTexts;
        const currentOption = options.find(item => item.id === optionId);
        if (currentOption) {
          pasters = currentOption.pasters;
          lineTexts = currentOption.lineTexts;
        }
      }

      const fontInnerHTML = rules.lineText?.fonts.map(item => `
        @font-face {
          font-weight: normal;
          font-style: normal;
          font-family: ${item.name};
          src: url('${item.url}');
        }
        @font-face {
          font-weight: normal;
          font-style: normal;
          font-family: ${item.name}-subset;
          src: url('${item.subsetUrl}');
        }
      `);

      return (
        <Fragment key={optionId}>
          <style>
            {fontInnerHTML}
          </style>
          <Content
            className={classnames(styles.mugContent, {
              [styles.mugContentActive]: current === index
            })}
            ref={this.refContent(index)}
            type={type}
            watermarkURL={watermarkURL}
            pasters={pasters}
            lineTexts={lineTexts}
            connects={pastersConnect}
            bottomImage={currentBottomImage}
            rules={rules}
            onSynchroObjects={this.handleSynchroObjects(index)}
            onSynchroActiveObject={this.handleSynchroActiveObject(index)}
            onPasterPress={this.handlePasterPress}
            onTextPress={this.handleTextPress}
          />
        </Fragment>
      );
    });
  }

  refContent = (index: number) => (node: Content | null) => {
    if (node)
      this._content[index] = node;
  }

  /** canvas渲染完成触发，同步数据给bottom */
  handleSynchroObjects = (index: number) => () => {
    const { synchroObjects } = this.state;
    const _content = this._content[index];
    synchroObjects[index] = _content.getObjects().map(object => ({
      object,
      dataURL: object.toDataURL({})
    }));
    this.setState({
      synchroObjects
    });
  }

  /** 同步当前激活数据给bottom */
  handleSynchroActiveObject = (index: number) => (target?: fabric.Object) => {
    const { synchroActiveObject } = this.state;
    synchroActiveObject[index] = target;
    this.setState({
      synchroActiveObject
    });
  }

  /** 贴纸BLPress */
  handlePasterPress = (paster: PasterOption) => {
    this.setState({
      drawerVisible: true,
      currentPaster: paster,
      editType: EditType.Paster,
      bottomLayoutVisible: false
    });
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'paster_edit'
    });
  }

  /**
   * 刻字BLPress
   */
  handleTextPress = (text: LineTextOption) => {
    this.setState({
      drawerVisible: true,
      currentText: text,
      editType: EditType.Text,
      bottomLayoutVisible: false
    });
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'text_edit'
    });
  }

  /** 刻字编辑完成 */
  handleTextApply = (lineText: LineTextOption) => {
    const { current, moduleState } = this.state;
    const _content = this._content[current];
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'text_apply'
    });
    _content.renderLineText(lineText);
    // state 数据同步
    const { options, optionId } = moduleState[current];
    moduleState[current].options = options.map(option => {
      if (option.id === optionId) {
        return {
          ...option,
          lineTexts: option.lineTexts.map(item => item.id === lineText.id ? lineText : item)
        };
      }
      return option;
    });
    moduleState[current].validator = this._moduleValidator(moduleState[current]);
    this.setState({
      moduleState
    });
    this.handleSynchroActiveObject(current)();
    this.handleDrawerClose();
  }

  /** 贴纸编辑完成 */
  handlPasterApply = (paster: PasterOption) => {
    const { current, moduleState } = this.state;
    const _content = this._content[current];
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'paster_apply'
    });
    _content.renderPaster(paster);
    // state 数据同步
    const { options, optionId } = moduleState[current];
    moduleState[current].options = options.map(option => {
      if (option.id === optionId) {
        return {
          ...option,
          pasters: option.pasters.map(item => item.rid === paster.rid ? paster : item)
        };
      }
      return option;
    });
    this.handleSynchroActiveObject(current)();
    this.handleDrawerClose();
  }

  /** bottom => content 同步激活数据 */
  handleActiveObjectChange = (target?: fabric.Object) => {
    const { current } = this.state;
    const _content = this._content[current];
    if (_content && target)
      _content.setActiveObject(target);
    this.handleSynchroActiveObject(current)(target);
  }

  // 向下一级
  handleSendBackwards = () => {
    const { current } = this.state;
    const _content = this._content[current];
    _content.sendBackwards();
    _content.discardActiveObject();
    this.handleSynchroActiveObject(current)();
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'paster_send_backwards'
    });
  }

  // 向上一级
  handleBringForward = () => {
    const { current } = this.state;
    const _content = this._content[current];
    _content.bringForward();
    _content.discardActiveObject();
    this.handleSynchroActiveObject(current)();
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'paster_bring_forward'
    });
  }

  // 最底部
  handleSendToBack = () => {
    const { current } = this.state;
    const _content = this._content[current];
    _content.sendToBack();
    _content.discardActiveObject();
    this.handleSynchroActiveObject(current)();
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'paster_send_to_back'
    });
  }

  // 最顶部
  handleBringToFront = () => {
    const { current } = this.state;
    const _content = this._content[current];
    _content.bringToFront();
    _content.discardActiveObject();
    this.handleSynchroActiveObject(current)();
    this.props.gtag('event', 'mug_edit_click', {
      'event_category': 'mug',
      'event_label': 'paster_bring_to_front'
    });
  }

  /**
   * 切换底图
   */
  handeBottomImageChange = (bottomImage: MugBottomImage) => {
    const { current } = this.state;
    const _content = this._content[current];
    _content.startLoading();
    this.setState({
      currentBottomImage: bottomImage
    });
  }

  /**
   * 关闭抽屉
   */
  handleDrawerClose = () => {
    this.setState({
      drawerVisible: false
    });
  }

  /** 切换模块 */
  handleModuleChange = (current: number) => {
    this.setState({
      current,
      bottomLayoutVisible: false
    }, () => {
      this.moduleValidator();
      this.props.gtag('event', 'mug_header_click', {
        'event_category': 'mug',
        'event_label': 'module'
      });
    });
  }

  /**
   * 切换当前模块的变体
   */
  handleVariantChange = (id: number) => {
    const { current, moduleState } = this.state;
    moduleState[current].optionId = id;
    this.setState({
      moduleState,
      bottomLayoutVisible: false
    }, () => {
      this.moduleValidator();
      this.props.gtag('event', 'mug_header_click', {
        'event_category': 'mug',
        'event_label': 'variant'
      });
    });
  }

  /**
   * 模块校验
   */
  moduleValidator = () => {
    const { current, moduleState } = this.state;
    moduleState[current].validator = this._moduleValidator(moduleState[current]);
    this.setState({
      moduleState
    });
  }

  /**
   * 模块校验
   */
  private _moduleValidator = (module: MugModuleState) => {
    const { rules, options, optionId } = module;
    const defaultOption = options.find(item => item.id === optionId) || options[0];
    if (rules.lineText && defaultOption)
      return Content.Validator(rules.lineText, defaultOption.lineTexts);
    return {
      status: ValidatorStatus.pass,
      message: []
    };
  }

  /** 下一步 */
  handleNext = async () => {
    const { current, moduleState, currentBottomImage } = this.state;
    const { validator } = moduleState[current];
    if (current === moduleState.length - 1) {
      if (moduleState.every(item => item.validator.status === ValidatorStatus.pass)) {
        const context = this._canvas.getContext('2d');
        context?.clearRect(0, 0, this._canvas.width, this._canvas.height);
        if (this._preview3d) {
          this.setState({
            previewVisible: true
          });
        } else {
          this.setState({
            uploadVisible: true
          });
        }
        // 并行生成面
        const produce = async () => {
          await Promise.all(this._content.map(async item => {
            await item?.toImage(context);
          }));
          return this._canvas.toDataURL();
        };
        let transparent = await produce();
        for (let i = 0; i < 3; i++) {
          if (transparent.length > 50000) break;
          transparent = await produce();
          this.props.gtag('event', 'mug_result_exception', {
            'event_category': 'mug',
            'event_label': 'blank_result'
          });
        }
        // 绘制底图
        context?.clearRect(0, 0, this._canvas.width, this._canvas.height);
        const transparentImage = await loadHTMLImage(transparent);
        const bottom = await loadHTMLImage(currentBottomImage.image);
        context?.drawImage(bottom, 0, 0);
        context?.drawImage(transparentImage, 0, 0);
        const result = this._canvas.toDataURL();
        // 生成预览图片
        context?.clearRect(0, 0, this._canvas.width, this._canvas.height);
        if (context) context.fillStyle = '#fff';
        context?.fillRect(0, 0, this._canvas.width, this._canvas.height);
        const resultImage = await loadHTMLImage(result);
        context?.drawImage(resultImage, 0, 0);
        if (this._preview3d) {
          // 生成3d预览图
          this.setState({
            previewVisible: true,
            previewSource: this._canvas.toDataURL('image/jpeg'),
          });
        } else {
          this.setState({
            uploadVisible: true
          });
        }
        // 上传预览图
        try {
          const stime = new Date().getTime();
          const result7n = await this.b64Upload7n(result, transparent);
          this.props.gtag('event', 'timing_complete', {
            'event_category': 'mug',
            'name': 'mug_upload_result',
            'value': new Date().getTime() - stime,
          });
          this.setState({
            result: result7n
          }, () => {
            if (!this._preview3d) // 如果不走3d预览 上传完成之后直接走确认回调
              this.handleConfirm();
          });
          return;
        } catch(e) {
          this.handlePreviewClose();
          alert(i18n.format('modules.error.upload.result'));
          this.props.gtag('event', 'mug_result_exception', {
            'event_category': 'mug',
            'event_label': 'upload_timeout'
          });
          return;
        }
      }
    } else {
      if (validator.status === ValidatorStatus.pass) {
        this.setState({
          current: current + 1
        }, () => {
          this.moduleValidator();
        });
        return;
      }
    }
    this.setState({
      oopVisible: true
    });
    this.props.gtag('event', 'mug_validator', {
      'event_category': 'mug',
    });
  }

  /** 关闭错误提示 */
  hanldeOopClose = () => {
    this.setState({
      oopVisible: false
    });
  }

  /** 关闭预览 */
  handlePreviewClose = () => {
    this.setState({
      previewVisible: false,
      uploadVisible: false,
      result: undefined
    });
    this.props.gtag('event', 'mug_preview_click', {
      'event_category': 'mug',
      'event_label': 'close_preview'
    });
  }

  /** 关闭引导 */
  handleGuideVisible = (visible: boolean) => {
    window.localStorage.setItem("_sunzi_mug_guide", "0");
    this.setState({
      guideVisible: visible
    });
    this.props.gtag('event', 'mug_guide_click', {
      'event_category': 'mug',
      'event_label': 'close_guide'
    });
  }

  /** bottom layout visible */
  handleBottomLayoutVisible = (visible: boolean) => {
    this.setState({
      bottomLayoutVisible: visible
    });
  }

  /** 完成回调 */
  handleConfirm = () => {
    const { result, moduleState, currentBottomImage } = this.state;
    let optionValue;
    const option: ConfirmOption[] = [];
    if (result) {
      moduleState.forEach((item, index) => {
        const _option = item.options.find(opt => opt.id === item.optionId);
        if (_option) {
          option.push({
            pasters: _option.pasters.map(paster => ({
              id: paster.id,
              annexs: paster.annexs.map(annex => annex.id)
            })),
            lineTexts: _option.lineTexts.map(lineText => ({
              value: lineText.value,
              font: lineText.font.name,
            }))
          });
          if (index === 0)
            optionValue = _option.value
        }
      });
      this.handlePreviewClose();
      this.props.onConfirm(result, {
        option,
        bottomImage: currentBottomImage.id
      }, optionValue);
      this.props.gtag('event', 'mug_preview_click', {
        'event_category': 'mug',
        'event_label': 'confirm_design'
      });
    }
  }

  /** 关闭回调 */
  handleClose = () => {
    const { moduleState } = this.state;
    const defaultOption = moduleState[0].options.find(opt => opt.id === moduleState[0].optionId);
    this.props.onClose(defaultOption && defaultOption.value);
    this.props.gtag('event', 'mug_header_click', {
      'event_category': 'mug',
      'event_label': 'close'
    });
  }

  render() {
    const { gtag, price, title, tips, pathGuide, bottomImages } = this.props;
    const { current, synchroObjects, synchroActiveObject, currentBottomImage, moduleState, drawerVisible,
      oopVisible, previewVisible, previewSource, guideVisible, bottomLayoutVisible, uploadVisible, result } = this.state;
    const disabled = moduleState.some(item => item.validator.status !== ValidatorStatus.pass);
    const isLastModule = current === moduleState.length - 1;
    const bottomNextDisabled = isLastModule ? disabled : moduleState[current].validator.status !== ValidatorStatus.pass;

    const SidebarContent = (
      <Sidebar onClose={this.handleDrawerClose}>
        {drawerVisible &&
          this.renderDrawer()}
      </Sidebar>
    );

    return (
      <div className="sunzi-mug">
        <Drawer
          sidebar={SidebarContent}
          open={drawerVisible}
          enableDragHandle={false}
          position="right"
        >
          <Header.Variant
            onClose={this.handleClose}
            current={current}
            modules={moduleState}
            onChange={this.handleModuleChange}
            onVariantChange={this.handleVariantChange}
          />
          <div className={styles.mugContentWrapper}>
            {this.renderContent()}
          </div>
          <Bottom
            gtag={gtag}
            tips={tips}
            currentBottomImage={currentBottomImage}
            bottomImages={bottomImages}
            onBottomImageChange={this.handeBottomImageChange}
            synchroActiveObject={synchroActiveObject[current]}
            synchroObjects={synchroObjects[current]}
            onActiveObjectChange={this.handleActiveObjectChange}
            isLastModule={isLastModule}
            bottomNextDisabled={bottomNextDisabled}
            bottomImageDisabled={moduleState[current].bottomDisabled}
            layoutVisible={bottomLayoutVisible}
            onLayoutVisible={this.handleBottomLayoutVisible}
            onNext={this.handleNext}
            onGuideOpen={() => this.handleGuideVisible(true)}
            sendBackwards={this.handleSendBackwards}
            bringForward={this.handleBringForward}
            sendToBack={this.handleSendToBack}
            bringToFront={this.handleBringToFront}
          />
          <OOP
            visible={oopVisible}
            onClose={this.hanldeOopClose}
            modules={moduleState}
            current={current}
          />
        </Drawer>
        {previewVisible &&
          <Wrapper>
            <Preview
              zen3d={this._zen3d}
              pathFBX={this._fbx}
              source={previewSource}
              uploading={result === undefined}
              onConfirm={this.handleConfirm}
              renderHeader={
                <Header
                  price={price}
                  title={title}
                  onClose={this.handlePreviewClose}
                />
              }
            />
          </Wrapper>
        }
        {uploadVisible &&
          <Wrapper>
            <div className={styles.mugUpload}>
              <div className={styles.mugUploadProgress}>
                <div className={styles.mugUploadProgressDone} />
              </div>
              <div className={styles.mugUploadText}>{i18n.format('modules.global.processing')}</div>
            </div>
          </Wrapper>
        }
        {guideVisible &&
          <Guide onClose={() => this.handleGuideVisible(false)}>
            <video width={240} height={240} autoPlay controls>
              <source src={pathGuide} type="video/mp4" />
            </video>
          </Guide>
        }
      </div>
    );
  }
}

export default Mug;
